import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Language, ReviewConfig, ReviewPosition, ReviewIntensity } from '../types';
import { policyService, Policy } from '../services/policyService';
import { useLanguage } from '../contexts/LanguageContext';

interface ConfigPanelProps {
    onStartAnalysis: (config: ReviewConfig) => void;
    isLoading?: boolean;
}

export const ConfigPanel: React.FC<ConfigPanelProps> = ({
    onStartAnalysis,
    isLoading = false
}) => {
    const { t, lang } = useLanguage();
    const [position, setPosition] = useState<ReviewPosition>('partyA');
    const [intensity, setIntensity] = useState<ReviewIntensity>('neutral');
    const [policies, setPolicies] = useState<Policy[]>([]);
    const [selectedPolicyId, setSelectedPolicyId] = useState<string>('');
    const [useCustomReview, setUseCustomReview] = useState(false);
    const [loadingPolicies, setLoadingPolicies] = useState(true);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
    const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

    // Load user policies
    useEffect(() => {
        const loadPolicies = async () => {
            setLoadingPolicies(true);
            try {
                const allPolicies = await policyService.getAllPolicies();
                setPolicies(allPolicies);
            } catch (e) {
                console.error('Failed to load policies:', e);
                setPolicies([]);
            } finally {
                setLoadingPolicies(false);
            }
        };
        loadPolicies();
    }, []);

    // 当开关关闭时，重置下拉框状态
    useEffect(() => {
        if (!useCustomReview) {
            setIsDropdownOpen(false);
        }
    }, [useCustomReview]);

    // 下拉打开时测量触发器位置，用于浮层定位
    useEffect(() => {
        if (!isDropdownOpen) {
            setDropdownRect(null);
            return;
        }
        const el = dropdownTriggerRef.current;
        if (!el) return;
        const update = () => {
            const r = el.getBoundingClientRect();
            setDropdownRect({ top: r.bottom, left: r.left, width: r.width });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [isDropdownOpen]);

    // 点击外部关闭下拉
    useEffect(() => {
        if (!isDropdownOpen) return;
        const onDocClick = () => setIsDropdownOpen(false);
        const t = setTimeout(() => document.addEventListener('click', onDocClick), 0);
        return () => {
            clearTimeout(t);
            document.removeEventListener('click', onDocClick);
        };
    }, [isDropdownOpen]);

    const handleStart = () => {
        const selectedPolicy = useCustomReview ? policies.find(p => p.id === selectedPolicyId) : undefined;
        onStartAnalysis({
            position,
            intensity,
            policyId: useCustomReview ? selectedPolicyId || undefined : undefined,
            policyContent: selectedPolicy?.content
        });
    };

    const positionOptions = [
        { value: 'partyA' as ReviewPosition, label: t('config.partyA'), desc: t('config.partyADesc') },
        { value: 'partyB' as ReviewPosition, label: t('config.partyB'), desc: t('config.partyBDesc') }
    ];

    const intensityOptions = [
        { value: 'aggressive' as ReviewIntensity, label: t('config.aggressive') },
        { value: 'neutral' as ReviewIntensity, label: t('config.neutral') },
        { value: 'defensive' as ReviewIntensity, label: t('config.defensive') }
    ];

    const intensityIndex = intensityOptions.findIndex(opt => opt.value === intensity);

    return (
        <div
            className="h-full flex flex-col relative overflow-hidden"
            style={{
                background: 'rgba(235, 245, 255, 0.18)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)'
            }}
        >
            {/* Scrollable Content Area - 垂直居中 */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar flex items-center justify-center">
                <div className="w-full max-w-md">
                    {/* Position Selection Card */}
                    <div
                        className="mb-4 p-5 rounded-2xl"
                        style={{
                            background: 'rgba(207, 211, 216, 0.1)',
                            backdropFilter: 'blur(40px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                        }}
                    >
                        <h3 className="text-slate-800 font-bold text-sm mb-4">
                            {t('config.position')}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {positionOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => setPosition(opt.value)}
                                    className="relative p-4 rounded-xl transition-all duration-300 text-left"
                                    style={{
                                        background: position === opt.value
                                            ? 'rgba(0, 0, 0, 0.65)'
                                            : 'rgba(207, 211, 216, 0.08)',
                                        backdropFilter: 'blur(30px) saturate(120%)',
                                        WebkitBackdropFilter: 'blur(30px) saturate(120%)',
                                        border: position === opt.value
                                            ? '1px solid rgba(0, 0, 0, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.3)',
                                        boxShadow: position === opt.value
                                            ? '0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.08)'
                                    }}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className={`font-bold text-base ${position === opt.value ? 'text-white' : 'text-slate-800'}`}>
                                                {opt.label}
                                            </div>
                                            <div className={`text-xs mt-1 truncate ${position === opt.value ? 'text-slate-200' : 'text-slate-600'}`}>{opt.desc}</div>
                                        </div>
                                        {position === opt.value && (
                                            <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 ml-2">
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Intensity Selection Card */}
                    <div
                        className="mb-4 p-5 rounded-2xl"
                        style={{
                            background: 'rgba(207, 211, 216, 0.1)',
                            backdropFilter: 'blur(40px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                        }}
                    >
                        <h3 className="text-slate-800 font-bold text-sm mb-4">
                            {t('config.intensity')}
                        </h3>
                        <div
                            className="relative p-1 rounded-2xl flex"
                            style={{
                                height: '48px',
                                background: 'rgba(207, 211, 216, 0.08)',
                                backdropFilter: 'blur(30px) saturate(120%)',
                                WebkitBackdropFilter: 'blur(30px) saturate(120%)',
                                border: '1px solid rgba(255, 255, 255, 0.3)'
                            }}
                        >
                            <div
                                className="absolute top-1 bottom-1 rounded-xl shadow-lg transition-all duration-300 ease-out"
                                style={{
                                    left: `calc(${intensityIndex * 33.33}% + 4px)`,
                                    width: 'calc(33.33% - 8px)',
                                    background: 'rgba(0, 0, 0, 0.65)',
                                    backdropFilter: 'blur(20px)',
                                    WebkitBackdropFilter: 'blur(20px)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                                }}
                            />
                            {intensityOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => setIntensity(opt.value)}
                                    className={`relative z-10 flex-1 py-2.5 text-sm font-bold text-center transition-colors duration-300 ${intensity === opt.value ? 'text-white' : 'text-slate-700 hover:text-slate-900'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-slate-600 mt-3 text-center">
                            {intensity === 'aggressive' && t('config.aggressiveTip')}
                            {intensity === 'neutral' && t('config.neutralTip')}
                            {intensity === 'defensive' && t('config.defensiveTip')}
                        </p>
                    </div>

                    {/* Custom Review Card - 容器大小固定 */}
                    <div
                        className="mb-4 rounded-2xl overflow-hidden"
                        style={{
                            background: 'rgba(207, 211, 216, 0.1)',
                            backdropFilter: 'blur(40px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                            minHeight: '120px'
                        }}
                    >
                        {/* 一行：左侧标题/描述或下拉框，右侧开关固定 */}
                        <div
                            className={`p-5 flex items-stretch justify-between gap-4 transition-all duration-300 ${policies.length > 0 ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
                                }`}
                            onClick={() => policies.length > 0 && setUseCustomReview(!useCustomReview)}
                            style={{ minHeight: '120px' }}
                        >
                            {/* 左侧：标题+描述（关时）或 磨砂下拉框（开时），overflow 隐藏 */}
                            <div className="flex-1 min-w-0 overflow-hidden relative">
                                {/* 标题+描述：打开时向右滑走消失 */}
                                <div
                                    className="absolute inset-0 flex flex-col justify-center transition-transform duration-300 ease-out"
                                    style={{
                                        transform: useCustomReview ? 'translateX(100%)' : 'translateX(0)'
                                    }}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-bold text-slate-800">
                                            {t('nav.managePolicies')}
                                        </span>
                                        <span className="text-xs text-slate-600">
                                            ({t('config.optional')})
                                        </span>
                                    </div>
                                    {policies.length === 0 && !loadingPolicies && (
                                        <p className="text-xs text-orange-600 font-medium">
                                            {t('policy.addFirst')}
                                        </p>
                                    )}
                                    {policies.length > 0 && !useCustomReview && (
                                        <p className="text-xs text-slate-600">
                                            {t('policy.enableTip')}
                                        </p>
                                    )}
                                </div>

                                {/* 磨砂下拉框：紧凑触发器 + 浮层选项列表（可超出容器） */}
                                {useCustomReview && policies.length > 0 && (
                                    <>
                                        <div
                                            className="absolute inset-0 flex items-center animate-fadeIn"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div
                                                className="w-full rounded-xl h-fit"
                                                style={{
                                                    background: 'rgba(207, 211, 216, 0.08)',
                                                    backdropFilter: 'blur(30px) saturate(120%)',
                                                    WebkitBackdropFilter: 'blur(30px) saturate(120%)',
                                                    border: '1px solid rgba(255, 255, 255, 0.25)'
                                                }}
                                            >
                                                {/* 下拉触发器：单行紧凑，上下居中 */}
                                                <button
                                                    ref={dropdownTriggerRef}
                                                    onClick={(e) => { e.stopPropagation(); setIsDropdownOpen(!isDropdownOpen); }}
                                                    className="w-full px-3 py-2 flex items-center justify-between rounded-xl transition-all duration-200 hover:bg-white/5"
                                                >
                                                    <div className="flex-1 min-w-0 text-left">
                                                        {selectedPolicyId ? (
                                                            <p className="text-sm font-bold text-slate-800 truncate">
                                                                {policies.find(p => p.id === selectedPolicyId)?.name ?? t('policy.select')}
                                                            </p>
                                                        ) : (
                                                            <p className="text-sm font-medium text-slate-500">
                                                                {t('policy.select')}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <svg
                                                        className={`w-4 h-4 text-slate-600 transition-transform duration-200 flex-shrink-0 ml-2 ${isDropdownOpen ? 'rotate-180' : ''
                                                            }`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* 下拉选项列表：Portal 浮层，可超出容器 */}
                                        {isDropdownOpen && dropdownRect && createPortal(
                                            <div
                                                className="fixed z-[9999] py-1 rounded-xl shadow-xl animate-fadeIn custom-scrollbar"
                                                style={{
                                                    top: dropdownRect.top,
                                                    left: dropdownRect.left,
                                                    width: dropdownRect.width,
                                                    maxHeight: '9.5rem',
                                                    overflowY: 'auto',
                                                    background: 'rgba(207, 211, 216, 0.12)',
                                                    backdropFilter: 'blur(30px) saturate(120%)',
                                                    WebkitBackdropFilter: 'blur(30px) saturate(120%)',
                                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="px-2 space-y-1">
                                                    {policies.map(policy => (
                                                        <button
                                                            key={policy.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedPolicyId(policy.id);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full px-3 py-2 rounded-lg text-left transition-all duration-200 hover:bg-white/10"
                                                            style={{
                                                                background: selectedPolicyId === policy.id
                                                                    ? 'rgba(0, 0, 0, 0.65)'
                                                                    : 'transparent',
                                                                border: selectedPolicyId === policy.id
                                                                    ? '1px solid rgba(0, 0, 0, 0.3)'
                                                                    : '1px solid transparent'
                                                            }}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`text-xs font-bold truncate ${selectedPolicyId === policy.id ? 'text-white' : 'text-slate-800'
                                                                        }`}>
                                                                        {policy.name}
                                                                    </p>
                                                                    <p className={`text-[10px] truncate mt-0.5 ${selectedPolicyId === policy.id ? 'text-slate-200' : 'text-slate-600'
                                                                        }`}>
                                                                        {policy.content.slice(0, 40)}...
                                                                    </p>
                                                                </div>
                                                                {selectedPolicyId === policy.id && (
                                                                    <div className="w-4 h-4 bg-white/20 rounded-full flex items-center justify-center ml-2 flex-shrink-0">
                                                                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>,
                                            document.body
                                        )}
                                    </>
                                )}
                            </div>

                            {/* 开关：位置固定，不随滑动 */}
                            <div className="flex-shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
                                <div
                                    className="relative w-12 h-7 rounded-full transition-all duration-300 cursor-pointer"
                                    style={{
                                        background: useCustomReview ? 'rgba(0, 0, 0, 0.65)' : 'rgba(207, 211, 216, 0.08)',
                                        backdropFilter: 'blur(30px) saturate(120%)',
                                        WebkitBackdropFilter: 'blur(30px) saturate(120%)',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        boxShadow: useCustomReview
                                            ? '0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.08)'
                                    }}
                                    onClick={() => policies.length > 0 && setUseCustomReview(!useCustomReview)}
                                >
                                    <div
                                        className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${useCustomReview ? 'left-6' : 'left-1'
                                            }`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 底部保留额外空间，防止滚动到底部时被视觉遮挡 */}
                    <div className="h-4"></div>

                    {/* Start Button - 作为最后一个卡片 */}
                    <div className="mt-6">
                        <button
                            onClick={handleStart}
                            disabled={isLoading}
                            className="w-full py-4 rounded-2xl font-bold text-base transition-all duration-300 flex items-center justify-center gap-3 text-white"
                            style={{
                                background: isLoading
                                    ? 'rgba(100, 116, 139, 0.8)'
                                    : 'rgba(0, 0, 0, 0.65)',
                                backdropFilter: 'blur(20px)',
                                WebkitBackdropFilter: 'blur(20px)',
                                boxShadow: isLoading
                                    ? '0 4px 12px rgba(0, 0, 0, 0.1)'
                                    : '0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(0, 0, 0, 0.3)',
                                cursor: isLoading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                                    {t('config.analyzing')}
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    {t('config.start')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
                
                /* 滚动条 */
                .custom-scrollbar::-webkit-scrollbar { 
                    width: 6px; 
                }
                .custom-scrollbar::-webkit-scrollbar-track { 
                    background: rgba(241, 245, 249, 0.3); 
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb { 
                    background: rgba(148, 163, 184, 0.4); 
                    border-radius: 10px; 
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { 
                    background: rgba(148, 163, 184, 0.6); 
                }
            `}</style>
        </div>
    );
};
